import * as Yup from "yup";
import { ABSOLUTE, CHARGE, PERCENTAGE } from "../constants/defaults";

export const createAllowanceChargeSchema = (
  currentNetAmount,
  itemBaseAmount,
  type,
  isTypeValueValidationNeeded = true,
  isForAllowanceForm = true,
  isDocumentLevel = false,
) => {
  return Yup.object({
    base_amount: Yup.number()
      .nullable()
      .when("type", {
        is: PERCENTAGE,
        then: (schema) =>
          schema
            .transform((value, originalValue) => {
              if (!originalValue) return null; // handle empty
              return value;
            })
            .required("Base amount is required")
            .min(0, "Base amount must be positive")
            .max(
              itemBaseAmount,
              `Base amount cannot exceed current item base amount (${itemBaseAmount})`,
            ),
        otherwise: (schema) => schema,
      }),
    ...(isTypeValueValidationNeeded && {
      type: Yup.string()
        .oneOf([PERCENTAGE, ABSOLUTE])
        .required("Type is required"),
      value: Yup.number()
        .transform((value, originalValue) => {
          if (!originalValue) return null;
          return value;
        })
        .required("Value is required")
        .min(0, "Value must be positive")
        .when("type", {
          is: PERCENTAGE,
          then: (schema) => {
            if (type === CHARGE) {
              // Charges with percentage
              return schema
                .min(0.01, "Percentage must be at least 0.01%")
                .test(
                  "negative-net-amount",
                  "This would make the net amount negative, which is not allowed",
                  function (value) {
                    if (value && this.parent.base_amount) {
                      const calculatedAmount =
                        (this.parent.base_amount * value) / 100;
                      const newNetAmount = currentNetAmount + calculatedAmount;
                      return newNetAmount >= 0;
                    }
                    return true;
                  },
                );
            } else {
              // Allowances with percentage
              return schema
                .min(1, "Percentage must be at least 1%")
                .max(100, "Percentage cannot exceed 100%")
                .test(
                  "max-amount",
                  "Allowance amount cannot be more than Current total before tax:",
                  function (value) {
                    if (value && this.parent.base_amount && currentNetAmount) {
                      const calculatedAmount =
                        (this.parent.base_amount * value) / 100;
                      return calculatedAmount <= currentNetAmount;
                    }
                    return true;
                  },
                )
                .test(
                  "negative-net-amount",
                  "This would make the net amount negative, which is not allowed",
                  function (value) {
                    if (value && this.parent.base_amount) {
                      const calculatedAmount =
                        (this.parent.base_amount * value) / 100;
                      const newNetAmount = currentNetAmount - calculatedAmount;
                      return newNetAmount >= 0;
                    }
                    return true;
                  },
                );
            }
          },
          otherwise: (schema) => {
            if (type === CHARGE) {
              // Absolute charges
              return schema.test(
                "negative-net-amount",
                "This would make the net amount negative, which is not allowed",
                function (value) {
                  if (value) {
                    const newNetAmount = currentNetAmount + value;
                    return newNetAmount >= 0;
                  }
                  return true;
                },
              );
            } else {
              // Absolute allowances
              return schema
                .max(
                  currentNetAmount,
                  `Value cannot exceed current net amount (${currentNetAmount})`,
                )
                .test(
                  "negative-net-amount",
                  "This would make the net amount negative, which is not allowed",
                  function (value) {
                    if (value) {
                      const newNetAmount = currentNetAmount - value;
                      return newNetAmount >= 0;
                    }
                    return true;
                  },
                );
            }
          },
        }),
    }),

    ...(isForAllowanceForm && {
      reason_code: Yup.string()
        .nullable()
        .test(
          "reason-required",
          "Either reason code or custom reason is required",
          function (value) {
            const { reason } = this.parent;
            return !!(value || reason);
          },
        ),

      reason: Yup.string()
        .nullable()
        .test(
          "reason-required",
          "Either reason code or custom reason is required",
          function (value) {
            const { reason_code } = this.parent;
            return !!(value || reason_code);
          },
        ),
    }),

    ...(isDocumentLevel && {
      tax_category: Yup.object()
        .shape({
          id: Yup.string().required(
            "Tax category is required for document-level charges and allowances",
          ),
          percent: Yup.number()
            .nullable()
            .min(0, "Tax rate must be positive")
            .max(100, "Tax rate cannot exceed 100%"),
          tax_exemption_reason_code: Yup.string()
            .nullable()
            .test(
              "tax-exemption-reason-code-required",
              "Tax exemption reason code is required when tax category is E",
              function (value) {
                const { id } = this.parent;
                if (id === "E") {
                  return value !== null && value !== undefined && value !== "";
                }
                return true;
              },
            ),
          tax_exemption_reason: Yup.string()
            .nullable()
            .test(
              "tax-exemption-reason-required",
              "Tax exemption reason is required when tax category is E",
              function (value) {
                const { id } = this.parent;
                if (id === "E") {
                  return value !== null && value !== undefined && value !== "";
                }
                return true;
              },
            ),
          zero_rated_reason_code: Yup.string()
            .nullable()
            .test(
              "zero-rated-reason-code-required",
              "Zero rated reason code is required when tax category is Z",
              function (value) {
                const { id } = this.parent;
                if (id === "Z") {
                  return value !== null && value !== undefined && value !== "";
                }
                return true;
              },
            ),
          zero_rated_reason: Yup.string()
            .nullable()
            .test(
              "zero-rated-reason-required",
              "Zero rated reason is required when tax category is Z",
              function (value) {
                const { id } = this.parent;
                if (id === "Z") {
                  return value !== null && value !== undefined && value !== "";
                }
                return true;
              },
            ),
        })
        .required(),
    }),
  });
};
